/**
 * The tier strip engine.
 *
 * Tiers publish themselves on data-tier-*, since a parent block cannot read its
 * children's settings; this is the first place that sees them all, so the
 * priority rule (a stepped tier wins) lives here. Positions are CSS. Every pass
 * derives ONE snapshot, tierState, that all consumers read.
 *
 * Transient state: lostGifts (losses that must clear a choice), rememberedPicks
 * (the shopper's clicks, one per tier, persisted in localStorage once the line
 * has landed: a re-earned tier repeats the last one, the next click rewrites
 * it, an emptied bag or a refused repeat clears it), pendingPick
 * (the newest answer no write has settled yet), queuedPick (a tap the lock
 * turned away), missedCart (the newest snapshot the write lock turned away,
 * reconciled when the lock lifts), reconciling (write lock).
 */

import { EVENTS, subscribe } from "ia-util-pubsub";
import { GRANT_MARK, cartHeld, countUnits, hasPurchasedLine, holdCart, isGrantedLine, refusalContext, spendOf } from "ia-cart-api";
import { captureError } from "ia-error-report";
import { srcsetFor } from "ia-util-helpers";

/** An add can resolve without landing: a dead variant 422s and the API swallows it. */
function confirmGiftLanded(cart, source, variantId, where) {
  // A refused or failed add answers with no cart and was already reported
  // where it was written (cart:add); a second event here tells it twice.
  if (!cart) return false;
  const landedLine = (cart?.items || []).find((item) => item.properties?._gwp_source === source);
  if (!landedLine) {
    const error = new Error(`gift ${variantId} for ${source} was due but did not land`);
    console.error("[ia-cart-tier]", error.message);
    captureError(error, { where, variant: String(variantId), source });
    return false;
  }
  if (landedLine.final_price > 0) {
    const priced = new Error(`gift ${variantId} for ${source} is priced in the cart, checkout will charge it`);
    console.error("[ia-cart-tier]", priced.message);
    captureError(priced, { where: "gift:priced", variant: String(variantId), source, price: landedLine.final_price });
  }
  return true;
}

/**
 * Per page, not per element: the drawer's teleport reconnects every descendant
 * and would refetch. The promise is cached, so two rows make one call.
 */
const variantCache = new Map();

/** /variants/ID.js through the page's cache; a failure leaves no entry behind. */
function fetchVariant(id) {
  if (!variantCache.has(id)) {
    const root = window.Shopify?.routes?.root || "/";
    const request = fetch(`${root}variants/${id}.js`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .catch((error) => {
        // A cached rejection would kill the same id in another tier's panel.
        variantCache.delete(id);
        throw error;
      });
    variantCache.set(id, request);
  }
  return variantCache.get(id);
}

/** `name` is product + variant, so the title is what is left once the variant part comes off. */
function productTitleOf(variant) {
  const option = variant.public_title || "";
  return option ? variant.name.replace(` - ${option}`, "") : variant.name;
}

/**
 * Grants per campaign per page. A healthy page grants a source once; a third
 * grant means something keeps taking the line (an app fighting the engine, a
 * priced gift being cleaned up) and the shopper sees it blink in and out.
 */
const grantCounts = new Map();

/**
 * The click memory outlives the page in localStorage, so a re-earned tier
 * repeats the pick after any navigation, and the row's mark paints with no
 * wait for the line. Blocked storage degrades to page-local silently.
 */
const PICKS_KEY = "ia-gift-picks";

function loadPicks() {
  try {
    return new Map(Object.entries(JSON.parse(localStorage.getItem(PICKS_KEY)) || {}));
  } catch {
    return new Map();
  }
}

function savePicks(picks) {
  try {
    if (picks.size === 0) localStorage.removeItem(PICKS_KEY);
    else localStorage.setItem(PICKS_KEY, JSON.stringify(Object.fromEntries(picks)));
  } catch {
    // Page-local memory still works for this page's lifetime.
  }
}

function countGrant(source, where) {
  const count = (grantCounts.get(source) || 0) + 1;
  grantCounts.set(source, count);
  if (count !== 3) return;
  const thrash = new Error(`the ${source} line was granted ${count} times on one page`);
  console.error("[ia-cart-tier]", thrash.message);
  captureError(thrash, { where, source, count });
}

class IaCartTiers extends HTMLElement {
  connectedCallback() {
    // One engine per drawer: the offers container is a second <ia-cart-tiers>
    // so an engine exists when the bar block is hidden, but only the first
    // connected instance runs. Its scope covers the other's anchors.
    const primary = this.scope.querySelector?.("ia-cart-tiers");
    if (primary && primary !== this) return;

    this.tiersCache = null;

    // Symmetric with disconnectedCallback: the drawer teleports itself to
    // <body> on connect, which reruns this for every descendant.
    this.unsubscribers?.forEach((off) => off());
    this.unsubscribers = [
      // From the event, never from iaCartAPI.cart: this element subscribes before
      // the API is built and would stay one cart behind.
      subscribe(EVENTS.cartChanged, (event) => {
        const cart = event.detail?.cartObj;
        this.renderSettled(cart);
        // Only ever on a confirmed cart: granting on a prediction would add a
        // real line for a state the server has not agreed to yet.
        this.reconcileGifts(cart);
      }),
      // Just this event's own updates: the API folds in every OTHER write
      // still in flight (iaCartAPI.pendingUpdates), so a burst of removals
      // predicts one consistent state instead of each undoing the last.
      subscribe(EVENTS.cartBeforeChange, (event) => this.render({ updates: event.detail?.updates })),
    ];

    // Delegated from the document: the Edit button lives on a cart line, and
    // those are replaced wholesale on every change.
    document.removeEventListener("click", this.onClick);
    this.onClick = this.handleClick.bind(this);
    document.addEventListener("click", this.onClick);

    // Survive the drawer's teleport, which reruns this for every descendant: a
    // fresh Set here would forget a tier the shopper had already lost, and the
    // remembered clicks come back from storage so navigation keeps them.
    this.lostGifts ||= new Set();
    this.rememberedPicks ||= loadPicks();

    this.hoistChoosers();
    this.fillPendingRows();
    this.prefetchSingleGifts();
    this.render();
    this.waitForApi();
  }

  /**
   * Rows configured by variant id: Liquid cannot resolve a variant back to its
   * product, so the row ships hidden and /variants/ID.js fills it. A gone or
   * sold-out variant takes its row with it.
   */
  fillPendingRows() {
    this.querySelectorAll("[data-gift-row-variant]").forEach(async (row) => {
      const id = row.dataset.giftRowVariant;

      try {
        const variant = await fetchVariant(id);
        if (!variant?.available) throw new Error("unavailable");

        const option = variant.public_title || "";
        row.querySelector("[data-gift-name]").textContent = productTitleOf(variant);

        const subtitle = row.querySelector("[data-gift-subtitle]");
        if (option) subtitle.textContent = option;
        else subtitle?.remove();

        row.querySelector("[data-gift-pick]")?.setAttribute("aria-label", `Choose ${variant.name}`);

        const src = variant.featured_image?.src;
        const media = row.querySelector("[data-gift-media]");
        if (src && media) {
          const url = new URL(src, window.location.origin);
          url.searchParams.set("width", "256");
          const image = document.createElement("img");
          image.src = url.toString();
          image.alt = "";
          image.width = 80;
          image.height = 80;
          image.loading = "lazy";
          media.replaceChildren(image);
        }

        delete row.dataset.giftRowVariant;
        row.hidden = false;
        const chooser = row.closest("[data-gift-chooser]");
        if (chooser) this.applySingle(chooser);
      } catch (error) {
        // Silent for the shopper, so the team hears it instead: a chooser short of
        // options means a dead variant id in the settings.
        console.error(`[ia-cart-tier] gift variant ${id} is not offerable`, error);
        captureError(new Error(`chooser variant ${id} is not offerable`), {
          where: "gift:variant",
          variant: String(id),
        });
        row.remove();
      }
    });
  }

  /** A tier block emits one stream, so its chooser lands in the markers grid. */
  hoistChoosers() {
    const panels = this.scope.querySelector("[data-tier-panels]");
    if (!panels) return;
    this.scope.querySelectorAll("[data-gift-chooser]").forEach((panel) => {
      if (panel.parentElement !== panels) panels.appendChild(panel);
    });
  }

  disconnectedCallback() {
    this.unsubscribers?.forEach((off) => off());
    this.unsubscribers = null;
    document.removeEventListener("click", this.onClick);
    clearTimeout(this.apiTimer);
    clearTimeout(this.settleTimer);
  }

  /**
   * Truth repaints wait for quiet. A burst of removals answers in stages (the
   * coalesced writes, then each reconciler taking its lines), and painting
   * every stage made the bar jump and the steps blink. Predictions (the
   * before-change render) stay instant; this only holds the CONFIRMED repaint
   * until nothing is in flight, then paints the latest cart once.
   */
  renderSettled(cart) {
    clearTimeout(this.settleTimer);
    const busy = () => {
      const api = window.iaCartAPI;
      return cartHeld() || Boolean(api?.pendingQty?.size) || Boolean(api?.qtyPumping);
    };
    if (!busy()) {
      this.render({ cart });
      return;
    }
    // Capped: a wedged hold must degrade to the old jumpiness, never to a bar
    // that stops following the server.
    const startedAt = Date.now();
    const tick = () => {
      if (!this.isConnected) return;
      if (busy() && Date.now() - startedAt < 5000) {
        this.settleTimer = setTimeout(tick, 120);
        return;
      }
      this.render({ cart: window.iaCartAPI?.cart || cart });
    };
    this.settleTimer = setTimeout(tick, 120);
  }

  handleClick(event) {
    // document-level, so a second drawer (an editor preview beside the page)
    // must not answer this one's taps.
    if (!this.scope.contains(event.target)) return;
    const edit = event.target.closest("[data-gift-edit]");
    if (edit) {
      this.openChooser(edit.dataset.giftEdit);
      return;
    }

    const toggle = event.target.closest("[data-gift-toggle]");
    if (toggle) {
      const chooser = toggle.closest("[data-gift-chooser]");
      const collapsing = toggle.getAttribute("aria-expanded") === "true";

      // Folding a reopened panel is Edit in reverse: it goes, the line comes back.
      if (collapsing && chooser?.hasAttribute("data-editing")) {
        chooser.removeAttribute("data-editing");
            return;
      }

      // Collapsing a list that already wears its answer folds onto it: the
      // shopper's tap, so the fold-by-tap rule holds, and "done choosing"
      // lands on the same view a pick does. Change reopens it.
      if (collapsing && chooser?.querySelector('[data-gift-pick][aria-pressed="true"]')) {
        chooser.setAttribute("data-chosen", "");
        return;
      }

      toggle.setAttribute("aria-expanded", collapsing ? "false" : "true");
      return;
    }

    const pick = event.target.closest("[data-gift-pick]");
    if (pick && !pick.disabled) this.chooseGift(pick);
  }

  /**
   * Swaps the gift: the new line goes in BEFORE the old comes out, or the bag
   * is giftless for the length of the add. A failed second call leaves two
   * lines, which reconcileGifts clears.
   *
   * Tapping the chosen row is not a write (a gift is not a toggle); tapping
   * the PRE-ADDED row is, it turns the default into an answer.
   */
  async chooseGift(button) {
    const api = window.iaCartAPI;
    const chooser = button.closest("[data-gift-chooser]");
    const tier = this.tierFor(chooser);
    if (!api || !tier) return;

    const variantId = button.dataset.giftPick;
    const source = tier.dataset.tierSource;

    // A tap that lands while a write holds the lock is kept, not swallowed:
    // a dropped tap reads as a broken button. Newest wins, on screen too.
    if (this.reconciling) {
      this.queuedPick = { source, variantId };
      this.pendingPick = { source, variantId };
      this.settleChooser(chooser, variantId);
      return;
    }
    const current = (api.cart?.items || []).find((item) => item.properties?._gwp_source === source);

    // The tap IS the answer: the panel folds onto the row tapped right away.
    chooser?.removeAttribute("data-editing");
    this.settleChooser(chooser, variantId);
    // A fresh answer settles any tier that was marked as lost.
    this.lostGifts.delete(source);

    // The bag's own answer, tapped again: nothing to write. A tap that waited
    // out the lock landed as pendingPick and must not stay pending past this.
    if (current && String(current.variant_id) === variantId && !current.properties?._gwp_auto) {
      this.pendingPick = null;
      this.rememberPick(source, variantId);
      return;
    }

    // Held until the pair of writes is done: mid-swap the bag carries both
    // gifts, and the tier would read the outgoing one as the answer.
    this.pendingPick = { source, variantId };
    this.reconciling = true;
    // Held for the pair, not per write: between them the bag holds both gifts
    // and a checkout tap would take the two of them.
    const release = holdCart();

    // Inside the try, all of it: a throw before it would leave the lock and the
    // hold standing, and the hold sits on checkout until the 4s cap.
    try {
      const cartObj = await api.addToCartJson({
        id: variantId,
        quantity: 1,
        properties: { _gwp_source: source, [GRANT_MARK]: "1" },
      });
      // Gated on the add having LANDED, or a failed add leaves the tier bare.
      if (confirmGiftLanded(cartObj, source, variantId, "gift:pick")) {
        // The CLICK is what the tier remembers, once the server holds it:
        // re-earning the tier repeats this pick.
        this.rememberPick(source, variantId);
        if (current) await api.updateQty({ key: current.key, qty: 0 });
      } else {
        // Dropped before the refresh, not in the finally: the render it
        // triggers would otherwise repaint the answer the server refused. The
        // fold goes too, or the panel stands folded on a gift the bag never took.
        this.pendingPick = null;
        chooser?.removeAttribute("data-chosen");
        await api.refresh?.().catch(() => {});
      }
    } catch (error) {
      // The paint is a lie the server never agreed to; only its render takes it
      // back. Caught here because nothing awaits chooseGift.
      console.error("[ia-cart-tier] gift swap failed", error);
      captureError(error, { ...refusalContext(error, "gift:swap"), variant: String(variantId), source });
      this.pendingPick = null;
      chooser?.removeAttribute("data-chosen");
      await api.refresh?.().catch(() => {});
    } finally {
      release();
      this.reconciling = false;
      this.pendingPick = null;
      this.replayQueuedPick();
      this.replayMissedCart();
    }
  }

  /** Replays the tap the lock turned away; nothing queued is a no-op. */
  replayQueuedPick() {
    const queued = this.queuedPick;
    this.queuedPick = null;
    if (!queued) return;
    const button = this.scope.querySelector?.(
      `[data-gift-chooser="${queued.source}"] [data-gift-pick="${queued.variantId}"]`
    );
    if (button && !button.disabled) this.chooseGift(button);
  }

  /** Reconciles the snapshot the lock turned away; nothing missed is a no-op. */
  replayMissedCart() {
    const missed = this.missedCart;
    this.missedCart = null;
    if (missed) this.reconcileGifts(missed);
  }

  /** Edit: `data-editing` overrides the panel's hidden state until the next pick. */
  openChooser(source) {
    const chooser = this.scope.querySelector(`[data-gift-chooser="${source}"]`);
    if (!chooser || chooser.hidden) return;
    chooser.setAttribute("data-editing", "");
    chooser.querySelector("[data-gift-toggle]")?.setAttribute("aria-expanded", "true");
    // The line hides on the same tap, not on the next cart change.
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    chooser.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "nearest" });
  }

  /**
   * A tier with one option has nothing to change to; CSS drops its Change.
   * Variant-id rows arrive hidden and are filled in later, so this runs again
   * from fillPendingRows rather than once per cart change.
   * @returns {Array} the rows a shopper can actually pick
   */
  applySingle(chooser) {
    const options = [...chooser.querySelectorAll("[data-gift-pick]")].filter(
      (pick) => !pick.closest(".ia-gift-chooser__row")?.hidden
    );
    chooser.toggleAttribute("data-single", options.length < 2);
    return options;
  }

  /** Folds the panel onto one row, marking it as the answer. */
  settleChooser(chooser, variantId) {
    if (!chooser) return;
    chooser.setAttribute("data-chosen", "");
    chooser.querySelectorAll("[data-gift-pick]").forEach((pick) => {
      pick.setAttribute("aria-pressed", String(pick.dataset.giftPick === String(variantId)));
    });
  }

  /** Cart setting: a block cannot read section settings, so it rides the drawer. */
  remembersGifts() {
    return this.closest("ia-cart")?.dataset.giftRemember !== "false";
  }

  /** The click memory takes a pick only while the setting keeps one. */
  rememberPick(source, variantId) {
    if (!this.remembersGifts()) return;
    this.rememberedPicks.set(source, String(variantId));
    savePicks(this.rememberedPicks);
  }

  forgetPick(source) {
    if (!this.rememberedPicks.delete(source)) return;
    savePicks(this.rememberedPicks);
  }

  /**
   * An emptied bag ends the shopping run and the click memory goes with it,
   * so the next run starts clean. Read off every confirmed cart, the seeded
   * one included: a checkout empties the bag with no event on this page, and
   * the next visit is the first to see the zero.
   */
  forgetPicksOnEmpty(cart) {
    if (!cart?.items || cart.items.length || !this.rememberedPicks?.size) return;
    this.rememberedPicks.clear();
    savePicks(this.rememberedPicks);
  }

  /**
   * Off by default: the panel collapses to the granted gift, and Change is what
   * opens the list. On lists every option upfront.
   */
  showsGiftOptions() {
    return this.closest("ia-cart")?.dataset.giftOptions === "true";
  }

  /** The tier a panel belongs to, by id rather than by where it happens to sit. */
  tierFor(chooser) {
    const id = chooser?.dataset.giftChooser;
    return id ? this.tiers.find((tier) => tier.dataset.tierSource === id) : null;
  }

  /**
   * Everything the strip decides about one bag, worked out ONCE.
   *
   * @param {object} [source] `{ cart }` for a confirmed cart, `{ updates }` for
   *   writes not sent yet. Predicted by default, so removing a line closes the
   *   panel with the same tap that shrinks the bar.
   * @returns {{lines: object[]|null, total: number, earned: boolean,
   *   stepsTier: Element|null,
   *   gifts: {tier: Element, source: string, active: boolean,
   *     line: object|null, chosen: object|null}[],
   *   lapsed: Set<string>, granted: boolean}}
   *   `lines` is null when the bag is not known yet. That is NOT an empty bag:
   *   read as empty, it once threw away the shopper's gift on page load.
   */
  tierState(source = {}) {
    const lines = this.bag(source);
    const total = this.total(lines);
    const earned = Boolean(lines) && hasPurchasedLine({ items: lines });
    const stepsTier = this.stepsTier;

    const gifts = this.tiers
      .filter((tier) => tier.dataset.tierType === "gift")
      .map((tier) => {
        const giftSource = tier.dataset.tierSource;
        const giftLine =
          (lines || []).find((item) => item.properties?._gwp_source === giftSource) || null;
        return {
          tier,
          source: giftSource,
          // A stepped tier takes the money tiers off the strip and their mechanics
          // with them; an offer was never on the strip. `data-tier-ineligible`
          // is Liquid's answer about the customer, which JS cannot see.
          active:
            Boolean(lines) &&
            (!stepsTier || tier.hasAttribute("data-tier-offer")) &&
            earned &&
            !tier.hasAttribute("data-tier-ineligible") &&
            this.conditionMet(tier, lines, total),
          line: giftLine,
          // Chosen means ANSWERED: a pre-added line carries `_gwp_auto` and is
          // nobody's choice yet, so the panel keeps inviting over it.
          chosen: giftLine && !giftLine.properties?._gwp_auto ? giftLine : null,
        };
      });

    // Tiers the bag no longer reaches, i.e. whose granted lines are on the way
    // out. Nothing lapses while the bag is unknown.
    const lapsed = new Set(
      lines ? gifts.filter((gift) => !gift.active).map((gift) => gift.source) : []
    );

    // Drives the strip's colour. Our lines answer through `lapsed` (one on its
    // way out counts for nothing), everything else through isGrantedLine, THE
    // shared answer: a local reading had drifted on `_gift_gwp: "false"`.
    //
    // `earned` gates the whole thing: with no purchase left, EVERY granted line
    // is on its way out (that is the reconcilers' rule), so the strip must drop
    // its mint on the tap that empties the bag, not a roundtrip later.
    const granted = earned && (lines || []).some((item) => {
      // Every gift counts, pre-added defaults included: the granted line is
      // mint whatever put it there, so a stricter reading here left the strip
      // beige beside a mint row.
      const giftSource = item.properties?._gwp_source;
      if (giftSource) return !lapsed.has(giftSource);
      return isGrantedLine(item);
    });

    return { lines, total, earned, stepsTier, gifts, lapsed, granted };
  }

  /** Spend by default; a trigger gift rides along with a product instead. */
  conditionMet(tier, lines, total) {
    const condition = tier.dataset.tierCondition;

    if (condition === "trigger") {
      const ids = new Set((tier.dataset.tierTriggerIds || "").split(",").filter(Boolean));
      if (!ids.size) return false;
      const field = tier.dataset.tierTriggerBy === "variant" ? "variant_id" : "product_id";
      return (lines || []).some((item) => ids.has(String(item[field])));
    }

    // "Buy 3, pick a gift": counted exactly the way the steps bar counts, so a
    // bar and a campaign configured alike can never disagree.
    if (condition === "units") {
      const goal = Number(tier.dataset.tierUnitsGoal) || 0;
      if (!goal) return false;
      const eligibleIds = (tier.dataset.tierUnitsEligible || "").split(",").filter(Boolean);
      return this.units(lines, {
        eligible: eligibleIds.length ? new Set(eligibleIds) : undefined,
        distinct: tier.hasAttribute("data-tier-units-distinct"),
        countSubscriptions: tier.hasAttribute("data-tier-units-subscriptions"),
      }) >= goal;
    }

    return total >= Number(tier.dataset.tierTarget);
  }

  /**
   * Applies one snapshot to the panels and the granted-line chrome.
   * @param {object} state - a tierState() snapshot
   */
  syncChoosers(state) {
    // The clearing write needs a confirmed cart, and by then the bag can be
    // over the threshold again: without this the panel looked like it had a
    // memory. Only a lapse with the shopper's ANSWER still in the bag marks a
    // loss: a pre-added default or a tier that was never granted has nothing
    // to forget, and an empty cart names every tier lapsed, which made the
    // next grant thrash (grant, forget-swap, grant again).
    if (!this.remembersGifts()) {
      state.gifts.forEach(({ source, line }) => {
        if (line && !line.properties?._gwp_auto && state.lapsed.has(source)) {
          this.lostGifts.add(source);
        }
      });
    }

    // One panel per gift tier, and the tier decides: no second opinion here.
    const showOptions = this.showsGiftOptions();
    state.gifts.forEach(({ source: giftSource, active, chosen, line }) => {
      const chooser = this.scope.querySelector(`[data-gift-chooser="${giftSource}"]`);
      if (!chooser) return;

      // In the same pass as the bar's own move: the panel answers the tap,
      // never the server or the bar's transition. Leaving the tier drops the
      // fold with it, so coming back opens on the list, but only a KNOWN bag
      // is a real lapse: before the API answers, this pass runs hidden too,
      // and dropping the fold then would erase the Liquid seed that lets a
      // page arriving with an answer paint folded.
      chooser.hidden = !active;
      if (chooser.hidden) {
        chooser.removeAttribute("data-editing");
        if (state.lines) chooser.removeAttribute("data-chosen");
        return;
      }

      // Only an ANSWER marks a row: a tier that just unlocked holds its
      // pre-added default, which nobody picked, and a choice the lapse
      // condemned (lostGifts) is no answer either: its line lives until the
      // clearing write lands. The bag's answer only MARKS the row; folding is
      // the tap's alone (settleChooser), the render merely keeps a fold that
      // is already there and drops it when the answer is gone, so nothing a
      // write races in can ever collapse the list under the shopper.
      const doomed = this.lostGifts.has(giftSource);
      const answered = showOptions || doomed ? null : chosen;
      // An unsettled pick outranks the bag: until the outgoing line goes, the
      // bag holds both and would name the one on its way out.
      const pending = this.pendingPick?.source === giftSource ? this.pendingPick : null;
      let answeredId = pending?.variantId || (answered ? String(answered.variant_id) : "");
      // The click's memory paints the mark the moment the tier is back: the
      // data is local, only the repeated line's write is remote. Guarded by
      // the row existing, so a dead variant never wears a mark, and by the
      // same show-options rule as the bag's answer, or the memory would keep
      // a fold that rule just refused.
      if (!answeredId && !showOptions && this.remembersGifts()) {
        const remembered = this.rememberedPicks.get(giftSource);
        if (remembered && chooser.querySelector(`[data-gift-pick="${remembered}"]`)) {
          answeredId = remembered;
        }
      }
      // A restored pick is not a tap: it marks its row, the list stays open.
      const tapPending = Boolean(pending) && !pending.restored;
      const folded = Boolean(answeredId) && (chooser.hasAttribute("data-chosen") || tapPending);
      chooser.toggleAttribute("data-chosen", folded);
      // Same answer the panel just read, printed for the header's counter.
      const count = chooser.querySelector("[data-gift-count]");
      if (count) count.textContent = `(${answeredId ? 1 : 0}/1)`;
      this.applySingle(chooser).forEach((pick) => {
        pick.setAttribute("aria-pressed", String(answeredId === pick.dataset.giftPick));
      });
    });

    this.syncGiftCards(state);
  }

  /**
   * A gift is shown once, in the tier wrapper: an answered chooser shows ours
   * as its chosen row, and every other GWP line (an app's, or ours when its
   * chooser is gone) gets a card built here from the line itself. The cart
   * line only comes back when neither exists, so nothing free is ever
   * invisible. Cards reconcile by line key, never rebuilt in place.
   * @param {object} state - a tierState() snapshot
   */
  syncGiftCards(state) {
    const panels = this.scope.querySelector("[data-tier-panels]");
    if (!panels || !state.lines) return;

    // The classifier's `granted` test (ia-cart-item-kind.liquid), so a carded
    // line always reads free and the price below asks about the price alone.
    const carded = new Map();
    state.lines.forEach((item) => {
      const source = item.properties?._gwp_source;
      const appMark = item.properties?._gift_gwp;
      if (!source && (!appMark || appMark === "false")) return;
      // A lapsed tier's gift is already condemned: it goes in the same pass
      // that takes the tier off, not a round trip later when the sweep lands.
      if (source && state.lapsed.has(source)) return;
      // A visible chooser already stands for its gift, as the invite over a
      // pre-added default or as the chosen row: only a gift with no panel of
      // its own needs a card.
      let inChooser = false;
      if (source) {
        const chooser = this.scope.querySelector(`[data-gift-chooser="${source}"]`);
        inChooser = Boolean(chooser) && !chooser.hidden;
      }
      if (!inChooser) carded.set(String(item.key), item);
    });

    panels.querySelectorAll("[data-gift-card]").forEach((card) => {
      const awaited = card.dataset.giftPending;
      if (awaited) {
        const arrived = state.lines.some((item) => item.properties?._gwp_source === awaited);
        if (arrived || state.lapsed.has(awaited)) card.remove();
        return;
      }
      if (carded.has(card.dataset.giftCard)) carded.delete(card.dataset.giftCard);
      else card.remove();
    });
    const strictPrice = this.closest("ia-cart")?.dataset.giftStrictPrice === "true";
    carded.forEach((item, key) => {
      const free = item.final_price === 0 || !strictPrice;
      panels.appendChild(buildGiftCard(item, key, free ? "FREE" : this.money(item.line_price), free));
    });

    this.scope.querySelectorAll("ia-cart-item[data-gift-line]").forEach((line) => {
      // A lapsed tier's line counts as housed too: reconcileGifts is removing
      // it in this same pass, and revealing it here flashes a doomed row into
      // the list for one roundtrip. It is free, so hiding it early costs the
      // totals nothing even if that write has to retry.
      const shown =
        panels.querySelector(`[data-gift-card="${line.dataset.key}"]`) ||
        (line.dataset.giftSource &&
          (state.lapsed.has(line.dataset.giftSource) ||
            this.scope.querySelector(`[data-gift-chooser="${line.dataset.giftSource}"]:not([hidden])`)));
      line.toggleAttribute("data-gift-in-panel", Boolean(shown));
    });
  }

  /**
   * On a cold load this runs before ia-core.js builds the API. The first paint
   * is still correct (Liquid left the total on the element), only the money
   * format is the fallback until the API lands. Bounded.
   */
  waitForApi(tries = 10) {
    const api = window.iaCartAPI;
    if (api) {
      // Against the seeded cart, so a hidden campaign's gift leaves on page load,
      // editor preview included.
      this.reconcileGifts(api.cart);
      return;
    }
    if (tries <= 0) return;
    this.apiTimer = setTimeout(() => {
      if (window.iaCartAPI) {
        this.render();
        this.reconcileGifts(window.iaCartAPI.cart);
      } else this.waitForApi(tries - 1);
    }, 100);
  }

  /** The drawer: campaigns live under two parent blocks and both must be seen. */
  get scope() {
    return this.closest?.("ia-cart") || this;
  }

  /** Cached per connect: uncached this ran six times a pass, 8ms against 2. */
  get tiers() {
    this.tiersCache ||= [...this.scope.querySelectorAll("[data-tier]")];
    return this.tiersCache;
  }

  /**
   * A stepped tier wins outright: a gift tier behind it would put a line in
   * the bag for a threshold the shopper is never shown.
   */
  get stepsTier() {
    return this.tiers.find((tier) => tier.dataset.tierType === "steps") || null;
  }

  get stepsWin() {
    return Boolean(this.stepsTier);
  }

  /**
   * THE one answer to "what is in the bag". Null means not known yet, which no
   * caller may confuse with empty.
   */
  bag(source = {}) {
    if (source.cart) return source.cart.items;
    const api = window.iaCartAPI;
    return api?.cart ? api.predictedLines(source.updates) : null;
  }

  /** Spend, not cart total. With no bag, Liquid's total is the first paint's truth. */
  total(lines) {
    return lines ? spendOf(lines) : Number(this.dataset.total) || 0;
  }

  units(lines, options) {
    return lines ? countUnits(lines, options) : 0;
  }

  render(source = {}) {
    // One snapshot for the pass: a rule added to tierState reaches every consumer.
    const state = this.tierState(source);
    if (state.stepsTier) this.renderSteps(state.stepsTier, state);
    else this.renderMoney(state);
    this.syncChoosers(state);
  }

  /* ---------- money tiers ---------- */

  renderMoney(state) {
    // Sorted by threshold, not by block order. Equal thresholds keep the
    // merchant's order (stable sort) and unlock together.
    const tiers = this.tiers
      .filter((tier) => tier.dataset.tierType !== "steps")
      // A trigger gift has no threshold and an ineligible tier no marker: neither
      // is a money milestone, so neither takes a money segment.
      .filter((tier) => tier.dataset.tierCondition !== "trigger")
      .filter((tier) => !tier.hasAttribute("data-tier-offer"))
      .filter((tier) => !tier.hasAttribute("data-tier-ineligible"))
      .sort((a, b) => Number(a.dataset.tierTarget) - Number(b.dataset.tierTarget));
    // Product gift markers: CSS puts an open one after the money markers
    // and an earned one before them.
    const products = this.tiers.filter(
      (tier) => tier.hasAttribute("data-tier-milestone") && !tier.hasAttribute("data-tier-ineligible")
    );
    if (!tiers.length && !products.length) return;

    const { total, lines } = state;
    tiers.forEach((tier) => {
      tier.toggleAttribute("data-tier-reached", total >= Number(tier.dataset.tierTarget));
    });
    // With the bag unknown, Liquid's first-paint answer stands.
    if (lines) {
      products.forEach((tier) => {
        const reached = this.conditionMet(tier, lines, total);
        tier.toggleAttribute("data-tier-reached", reached);
        const link = tier.querySelector("[data-tier-product-link]");
        if (link) link.inert = reached;
      });
    }

    this.markLast([...tiers, ...products]);

    const nextIndex = tiers.findIndex((tier) => total < Number(tier.dataset.tierTarget));
    const moneyDone = nextIndex === -1;
    const earnedProducts = products.filter((tier) => tier.hasAttribute("data-tier-reached"));
    const nextProduct = products.find((tier) => !tier.hasAttribute("data-tier-reached"));
    const unlocked = moneyDone && !nextProduct;
    this.toggleAttribute("data-unlocked", unlocked);

    // Segments left to right: earned product tiers (CSS moves them to the front),
    // the money tiers, then the product tiers still open.
    const segment = 100 / (tiers.length + products.length);
    let fill = 100;
    if (!moneyDone) {
      fill = segment * earnedProducts.length + (this.fillPercent(tiers, nextIndex, total) * tiers.length * segment) / 100;
    } else if (nextProduct) {
      fill = segment * (earnedProducts.length + tiers.length);
    }
    this.setFill(fill);

    // While filling, the emphasis is the DISTANCE; the prize takes it once won.
    // A product tier earned ahead of the money says so first, as in Beekeepers.
    if (unlocked) {
      this.setHeading(
        this.template("textUnlocked").replace("[rewards]", this.rewardList([...earnedProducts, ...tiers]))
      );
    } else if (!moneyDone) {
      const ahead = earnedProducts.length
        ? `${this.template("textUnlocked").replace("[rewards]", this.rewardList(earnedProducts))} `
        : "";
      this.setHeading(
        ahead +
          this.template("textAway")
            .replace("[reward]", tiers[nextIndex].dataset.tierReward || "")
            .replace(
              "[amount]",
              this.amount(this.money(Number(tiers[nextIndex].dataset.tierTarget) - total))
            )
      );
    } else {
      this.setHeading(
        (nextProduct.dataset.tierTextAway || "")
          .replace("[product]", this.productLink(nextProduct))
          .replace("[reward]", this.reward(nextProduct.dataset.tierReward || ""))
      );
    }
  }

  /**
   * The marker that ends the bar carries `data-tier-last`, so its labels grow
   * leftwards and stay inside the drawer. Found by the same keys as the CSS
   * `order` in ia-progress-bar.css (earned product -1, money 0, open product 1),
   * then block order.
   */
  markLast(markers) {
    const rank = (tier) => {
      if (!tier.hasAttribute("data-tier-milestone")) return 0;
      return tier.hasAttribute("data-tier-reached") ? -1 : 1;
    };
    const shown = this.tiers.filter((tier) => markers.includes(tier));
    const last = shown.reduce((found, tier) => (found && rank(found) > rank(tier) ? found : tier), null);
    this.tiers.forEach((tier) => tier.toggleAttribute("data-tier-last", tier === last));
  }

  /**
   * Piecewise: whole segments for the tiers passed, plus the fraction of the
   * current one. A single linear ratio puts the fill and the marker in
   * different places, which is what "the bar lies" looks like.
   */
  fillPercent(tiers, nextIndex, total) {
    const segment = 100 / tiers.length;
    const from = nextIndex === 0 ? 0 : Number(tiers[nextIndex - 1].dataset.tierTarget);
    const to = Number(tiers[nextIndex].dataset.tierTarget);
    const within = to > from ? (total - from) / (to - from) : 0;
    return segment * nextIndex + segment * Math.min(Math.max(within, 0), 1);
  }

  /** The emphasis belongs to the code: a merchant bolding it by hand drifts. */
  reward(name) {
    return name ? `<span class="ia-progress__reward">${name}</span>` : "";
  }

  /** An open Product gift names its product as a link to it: one tap to add. */
  productLink(tier) {
    const name = tier.dataset.tierProduct || "";
    const url = tier.dataset.tierProductUrl;
    if (!name || !url) return this.amount(name);
    const link = document.createElement("a");
    link.className = "ia-progress__amount";
    link.href = url;
    link.textContent = name;
    return link.outerHTML;
  }

  /** The not-yet emphasis: how far is left, not the prize. */
  amount(html) {
    return html ? `<span class="ia-progress__amount">${html}</span>` : "";
  }

  /** Wraps [count] AND its noun. Runs before [count] is substituted. */
  emphasizeCount(template) {
    return template.replace(/\[count\](\s+\p{L}+)?/u, (match) => this.amount(match));
  }

  /** [reward] takes a following "!" inside the emphasis; [rewards] is left alone. */
  fillReward(template, name) {
    return template.replace(/\[reward\]!*/, (match) =>
      this.reward(name ? name + match.slice("[reward]".length) : "")
    );
  }

  /** "free shipping and free gift", or a comma list for three and more. */
  rewardList(tiers) {
    const names = tiers.map((tier) => this.reward(tier.dataset.tierReward)).filter(Boolean);
    if (names.length < 2) return names[0] || "";
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }

  /* ---------- automatic gifts ---------- */

  /**
   * Every gift against the bag in one pass: grant, take back, clamp. One
   * reconciler, not one per campaign, or two of them add on one change.
   *
   * A tier with several products pre-adds the FIRST, marked `_gwp_auto`: real
   * from the moment it is earned, invisible until the shopper answers.
   */
  async reconcileGifts(cart) {
    const api = window.iaCartAPI;
    if (!api || !cart?.items) return;
    // Never on the page's own seed: a gift the server holds and the seed lacks
    // would be granted twice. The newest cart is the one to read afterwards.
    await api.verified;
    cart = api.cart || cart;
    this.forgetPicksOnEmpty(cart);
    // The lock drops this PASS, not the change: the newest snapshot it turned
    // away is reconciled when the lock lifts, and a settled bag reruns as a
    // no-op, so the replay always converges.
    if (this.reconciling) {
      this.missedCart = cart;
      return;
    }

    // Every gift tier, read where the panel reads: taking a gift back applies to
    // a chosen one exactly as much. Only the granting is the shopper's business.
    const { gifts } = this.tierState({ cart });

    // A hidden or removed campaign would leave its line in the bag forever. Only
    // OUR lines are orphans; `_gift_gwp` belongs to apps. Runs with zero
    // campaigns too.
    const known = new Set(gifts.map((gift) => gift.source));
    const orphans = cart.items.filter(
      (item) => item.properties?._gwp_source && !known.has(item.properties._gwp_source)
    );

    // A sample's only cleaner is its picker, which dies with its block: with no
    // picker in the drawer the line is as orphaned as a hidden campaign's gift.
    if (!this.scope.querySelector?.("ia-cart-samples")) {
      orphans.push(...cart.items.filter((item) => item.properties?._sample_source));
    }

    if (!gifts.length && !orphans.length) return;

    this.reconciling = true;
    // Removals and grants are separate writes, so checkout waits for the set.
    const release = holdCart();
    try {
      // One write for every removal and clamp: a roundtrip repaints the drawer.
      // Adds stay separate, /cart/add.js takes one shape at a time here.
      const updates = {};
      const adds = [];
      const lostSettled = [];

      orphans.forEach((orphan) => {
        updates[orphan.key] = 0;
      });

      for (const { tier, source, active: deserved } of gifts) {
        const variants = (tier.dataset.tierGiftVariants || "").split(",").filter(Boolean);

        // A line we never added is not ours to keep: no provenance mark, or a
        // product outside the campaign's variants (which a copied mark cannot
        // fake). Ahead of the newest-wins dedupe, so a forged newest never
        // displaces the real line.
        const claimed = cart.items.filter((item) => item.properties?._gwp_source === source);
        const lines = claimed.filter((item) => {
          const marked = Boolean(item.properties?.[GRANT_MARK]);
          const listed = !variants.length || variants.includes(String(item.variant_id));
          return marked && listed;
        });
        claimed
          .filter((item) => !lines.includes(item))
          .forEach((item) => {
            updates[item.key] = 0;
          });
        if (claimed.length > lines.length) {
          const swept = claimed.filter((item) => !lines.includes(item));
          const forged = new Error(`${swept.length} gift line(s) the engine never added, removing`);
          console.error("[ia-cart-tier]", forged.message, source);
          captureError(forged, {
            where: "gift:foreign",
            source,
            count: swept.length,
            variants: swept.map((item) => item.variant_id).join(","),
          });
        }

        // A half-failed swap leaves two lines; the newest is the answer, and
        // cart.items is newest FIRST. Normal swaps hold `reconciling` instead.
        const line = lines[0] || null;
        lines.slice(1).forEach((extra) => {
          updates[extra.key] = 0;
        });
        if (lines.length > 1) {
          // One campaign, one line is the promise; more of them in a real
          // bag means a swap lost its second half or two tabs raced.
          const dupes = new Error(`${lines.length} lines for one campaign, keeping the newest`);
          console.error("[ia-cart-tier]", dupes.message, source);
          captureError(dupes, {
            where: "gift:dupes",
            source,
            count: lines.length,
            variants: lines.slice(0, 10).map((item) => item.variant_id).join(","),
          });
        }

        // Only a chooser can be forgotten: with one product there is nothing to
        // pick again and the branch below would grant it straight back.
        const forget = this.lostGifts.has(source) && variants.length !== 1;

        if (deserved && !line) {
          this.lostGifts.delete(source);
          if (!variants.length) continue;
          // The remembered CLICK comes back AS the answer, no `_gwp_auto`. The
          // memory lives until the next click, so every re-earn repeats it; a
          // campaign that no longer lists the variant falls through to the
          // default (and a repeat that fails to land clears it below, with
          // the default stepping in).
          const remembered = this.remembersGifts() ? this.rememberedPicks.get(source) : null;
          if (remembered && variants.length > 1 && variants.includes(remembered)) {
            adds.push({
              id: remembered,
              properties: { _gwp_source: source, [GRANT_MARK]: "1" },
              source,
              remembered: true,
              fallback: variants[0],
            });
            continue;
          }
          // One product grants itself; several pre-add the first as `_gwp_auto`, so
          // the panel keeps inviting until a pick replaces the default.
          const properties = { _gwp_source: source, [GRANT_MARK]: "1" };
          if (variants.length > 1) properties._gwp_auto = "1";
          adds.push({ id: variants[0], properties, source });
        } else if ((!deserved || forget) && line) {
          // Below the threshold is the bag's normal ebb; a line while the tier
          // is INELIGIBLE is not: a forged add, a revoked tag, or a campaign
          // reconfigured under the shopper. Removed either way, reported here.
          if (tier.hasAttribute("data-tier-ineligible")) {
            const wrongGroup = new Error("a gift line for a customer group the shopper is not in, removing");
            console.error("[ia-cart-tier]", wrongGroup.message, source);
            captureError(wrongGroup, {
              where: "gift:ineligible",
              source,
              variant: String(line.variant_id),
            });
          }
          updates[line.key] = 0;
          // The mark comes off when the removal LANDS, not when it is sent: a
          // settle-timer repaint in between would read the still-living line
          // as the answer and fold the panel on the OLD pick.
          lostSettled.push(source);
          // Forgetting resets to the default: an empty slot until the next cart
          // change would read as the gift being lost.
          if (deserved && variants.length > 1) {
            adds.push({
              id: variants[0],
              properties: { _gwp_source: source, _gwp_auto: "1", [GRANT_MARK]: "1" },
              source,
            });
          }
        } else if (deserved && line && line.quantity > 1) {
          // Two tabs, or a double tap on a stale page: a gift is one.
          updates[line.key] = 1;
          const grew = new Error(`a gift line grew to quantity ${line.quantity}, clamping to one`);
          console.error("[ia-cart-tier]", grew.message, source);
          captureError(grew, { where: "gift:clamped", source, quantity: line.quantity });
        }
      }

      // The restored answer rides pendingPick over the gap between the writes:
      // the removal's repaint sees no line yet and would drop the row's mark
      // for a frame. `restored` keeps it from FOLDING the list: only a tap
      // folds, a repeat of the old pick just marks its row in the open list.
      // A real tap overwrites it, newest wins.
      const restored = adds.find((add) => add.remembered);
      if (restored && !this.pendingPick) {
        this.pendingPick = { source: restored.source, variantId: restored.id, restored: true };
        this.restoredPending = this.pendingPick;
      }

      if (Object.keys(updates).length) await api.updateCart({ data: { updates } });
      adds.forEach((add) => {
        countGrant(add.source, "gift:thrash");
        this.showPendingGift(add.source);
      });
      // A repeat of the remembered click that never lands clears the memory,
      // or every re-earn would retry a variant the server keeps refusing.
      const fallbacks = [];
      const confirmAdd = (cartObj, add) => {
        const landed = confirmGiftLanded(cartObj, add.source, add.id, "gift:grant");
        if (!landed) this.dropPendingGift(add.source);
        if (!landed && add.remembered) {
          this.forgetPick(add.source);
          fallbacks.push(add);
        }
      };
      if (adds.length === 1) {
        const { id, properties } = adds[0];
        const granted = await api.addToCartJson({ id, quantity: 1, properties });
        confirmAdd(granted, adds[0]);
      } else if (adds.length > 1) {
        // Simultaneous grants ride ONE add: sequential calls made the second
        // gift appear a full roundtrip after the first.
        const granted = await api.addToCartJson({
          items: adds.map(({ id, properties }) => ({ id, quantity: 1, properties })),
        });
        if (granted) {
          adds.forEach((add) => confirmAdd(granted, add));
        } else {
          // A multi-item add is refused whole, so one dead variant must not
          // cost the other campaigns their gift: the batch degrades to singles.
          for (const add of adds) {
            const solo = await api.addToCartJson({ id: add.id, quantity: 1, properties: add.properties });
            confirmAdd(solo, add);
          }
        }
      }

      // A refused repeat gets the default in this same pass, as `_gwp_auto`:
      // a failed add publishes no cart change, so left to the next one the
      // earned tier would sit with no gift line at all.
      for (const add of fallbacks) {
        const properties = { _gwp_source: add.source, _gwp_auto: "1", [GRANT_MARK]: "1" };
        const granted = await api.addToCartJson({ id: add.fallback, quantity: 1, properties });
        confirmGiftLanded(granted, add.source, add.fallback, "gift:grant");
      }

      // A failed write skips this on purpose: the mark stays and the retry
      // pass forgets again.
      lostSettled.forEach((source) => this.lostGifts.delete(source));
    } catch (error) {
      // Without this, a failed write died as an unhandled rejection: nothing
      // awaits reconcileGifts, so nothing else would ever report it.
      this.dropPendingGift();
      console.error("[ia-cart-tier] gift reconcile failed", error);
      captureError(error, refusalContext(error, "gift:reconcile"));
    } finally {
      // Only the restore's own pendingPick is dropped: a tap that landed
      // mid-reconcile replaced it and is settled by its own write.
      if (this.pendingPick && this.pendingPick === this.restoredPending) this.pendingPick = null;
      this.restoredPending = null;
      release();
      this.reconciling = false;
      this.replayQueuedPick();
      this.replayMissedCart();
    }
  }

  /**
   * The grant is a second roundtrip after the trigger's own add. A single gift
   * is known in advance, so its card shows as the add goes out and
   * syncGiftCards swaps it for the real one when the line lands. A chooser
   * needs nothing: it is already on screen with its rows.
   */
  async showPendingGift(source) {
    const panels = this.scope.querySelector("[data-tier-panels]");
    const tier = this.tiers.find((candidate) => candidate.dataset.tierSource === source);
    const variants = (tier?.dataset.tierGiftVariants || "").split(",").filter(Boolean);
    if (!panels || variants.length !== 1) return;

    let variant;
    try {
      variant = await fetchVariant(variants[0]);
    } catch {
      return;
    }
    // The answer can lose the race to the line itself, or to a failed grant.
    const landed = window.iaCartAPI?.cart?.items?.some((item) => item.properties?._gwp_source === source);
    if (landed || !this.reconciling) return;
    if (this.scope.querySelector(`[data-gift-chooser="${source}"]:not([hidden])`)) return;
    if (panels.querySelector(`[data-gift-pending="${source}"]`)) return;

    const preview = {
      product_title: productTitleOf(variant),
      variant_title: variant.public_title || "",
      image: variant.featured_image?.src || "",
      featured_image: { width: variant.featured_image?.width || 0 },
    };
    const card = buildGiftCard(preview, `pending:${source}`, "FREE", true);
    card.dataset.giftPending = source;
    card.setAttribute("aria-busy", "true");
    panels.appendChild(card);
  }

  /** Single gifts are fetched ahead, so the pending card needs no wait of its own. */
  prefetchSingleGifts() {
    const ids = this.tiers
      .map((tier) => (tier.dataset.tierGiftVariants || "").split(",").filter(Boolean))
      .filter((variants) => variants.length === 1)
      .map(([id]) => id);
    if (!ids.length) return;
    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));
    idle(() => ids.forEach((id) => fetchVariant(id).catch(() => {})));
  }

  /** A grant that did not land takes its card back; no source means every one. */
  dropPendingGift(source) {
    const selector = source ? `[data-gift-pending="${source}"]` : "[data-gift-pending]";
    this.scope.querySelectorAll(selector).forEach((card) => card.remove());
  }

  /* ---------- stepped tier ---------- */

  renderSteps(steps, state) {
    const goal = Number(steps.dataset.tierGoal) || 0;
    const eligibleIds = (steps.dataset.tierEligible || "").split(",").filter(Boolean);
    const done = Math.min(this.units(state.lines, {
      countSubscriptions: steps.hasAttribute("data-tier-count-subscriptions"),
      eligible: eligibleIds.length ? new Set(eligibleIds) : undefined,
      distinct: steps.dataset.tierCountMode === "items",
    }), goal);

    // Hidden, not just unstyled: they would leak through the unlocked banner.
    this.tiers.forEach((tier) => {
      if (tier !== steps) tier.hidden = true;
    });
    steps.hidden = false;

    this.markSteps(steps, done);
    // `goal > 0`: an unnamed step list gives 0, and `done >= goal` would then be
    // true on an empty bag.
    const unlocked = goal > 0 && done >= goal;
    this.toggleAttribute("data-unlocked", unlocked);
    this.setFill(this.stepsPercent(done, goal));

    if (unlocked) {
      this.setHeading(this.fillReward(this.template("textStepsUnlocked"), steps.dataset.tierReward));
      return;
    }

    // The merchant picks: the count runs down as the bag fills, or names the
    // goal throughout. [s] goes first so the noun is finished before wrapping.
    const remaining = goal - done;
    const shown = this.dataset.stepsCount === "goal" ? goal : remaining;
    this.setHeading(
      this.emphasizeCount(
        this.template("textStepsAway").replaceAll("[s]", shown === 1 ? "" : "s")
      )
        .replaceAll("[count]", String(shown))
        .replaceAll("[left]", String(remaining))
        .replace("[reward]", steps.dataset.tierReward || "")
    );
  }

  /**
   * Circle k sits at k/goal of the rail, and the design runs the fill halfway
   * past it, so the bar always says "there is more". An empty bag fills none.
   */
  stepsPercent(done, goal) {
    if (!goal || !done) return 0;
    if (done >= goal) return 100;
    return ((done + 0.5) / goal) * 100;
  }

  /** Liquid rendered the steps; this only says which are done. */
  markSteps(steps, done) {
    const list = steps.querySelector("[data-tier-steps]");
    if (!list) return;
    [...list.children].forEach((item, index) => {
      item.toggleAttribute("data-done", index < done);
    });
  }

  /* ---------- shared ---------- */

  /**
   * The fill, and the rail's own coming and going. Unlocking hides the rail
   * (ia-progress-bar.css keys on `data-rail-hidden`) in the SAME pass that
   * opens the chooser, so the strip and the panel trade places as one change,
   * nothing in stages. The sweep back keeps its motion: losing the tier puts
   * the rail back at full first, and the slide down says what was lost.
   * Reduced motion skips the sweep and just sets the state.
   */
  setFill(percent) {
    const fill = this.querySelector("[data-tier-fill]");
    if (!fill) return;
    const next = Math.min(Math.max(percent, 0), 100);
    const first = this.filled == null;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const moving = fill.style.width !== `${next}%`;

    if (first || still) {
      fill.removeAttribute("data-tier-rewinding");
      fill.style.width = `${next}%`;
      this.toggleAttribute("data-rail-hidden", next === 100);
      this.filled = next;
      return;
    }

    const wasFull = this.filled === 100;
    const rewinding = wasFull && next < 100;
    if (rewinding) {
      // Back into the layout at full, so the sweep has a place to start from.
      this.removeAttribute("data-rail-hidden");
      fill.style.width = "100%";
      // Read forces the two writes into separate frames; without it the browser
      // coalesces them and the transition never runs.
      void fill.offsetWidth;
    }
    if (moving) fill.toggleAttribute("data-tier-rewinding", rewinding);
    if (rewinding) {
      fill.addEventListener(
        "transitionend",
        () => fill.removeAttribute("data-tier-rewinding"),
        { once: true }
      );
    }
    fill.style.width = `${next}%`;
    this.filled = next;
    this.toggleAttribute("data-rail-hidden", next === 100);
  }

  setHeading(html) {
    const heading = this.querySelector("[data-tier-heading]");
    if (heading && heading.innerHTML !== html) heading.innerHTML = html;
  }

  template(name) {
    return this.dataset[name] || "";
  }

  money(cents) {
    // One formatter in the theme, or the shop's own format drifts.
    return window.iaCartAPI?.formatPrice
      ? window.iaCartAPI.formatPrice(cents)
      : `${(cents / 100).toFixed(2)}`;
  }
}

/** The card's media column, the same 44 the chooser rows use (ia-cart-gift.css). */
const GIFT_CARD_SLOT = 44;

/**
 * A wrapper card for a GWP line no chooser shows, from the line's own data.
 * The card has no Change to offer, so its right cell carries the price the
 * caller resolved under the same rule the cart lines follow.
 */
function buildGiftCard(item, key, priceText, isFree) {
  const card = document.createElement("div");
  card.className = "ia-gift-chooser__row";
  card.dataset.giftCard = key;

  const media = document.createElement("div");
  media.className = "ia-gift-chooser__media ia-ratio";
  if (item.image) {
    const img = document.createElement("img");
    const srcset = srcsetFor(item.image, item.featured_image?.width || 0, GIFT_CARD_SLOT);
    if (srcset) {
      img.srcset = srcset;
      img.sizes = `${GIFT_CARD_SLOT}px`;
    }
    img.src = item.image + (item.image.includes("?") ? "&" : "?") + `width=${GIFT_CARD_SLOT * 2}`;
    img.alt = "";
    img.loading = "lazy";
    media.appendChild(img);
  }

  const info = document.createElement("div");
  info.className = "ia-gift-chooser__info";
  const name = document.createElement("p");
  name.className = "ia-gift-chooser__name";
  name.textContent = item.product_title || "";
  info.appendChild(name);

  // The same element a chooser row carries, so the section's flag reaches a
  // card through the one CSS rule and both read alike.
  if (item.variant_title && item.variant_title !== "Default Title") {
    const subtitle = document.createElement("p");
    subtitle.className = "ia-gift-chooser__subtitle ia-text-xs";
    subtitle.textContent = item.variant_title;
    info.appendChild(subtitle);
  }

  const price = document.createElement("span");
  price.className = "ia-gift-chooser__price";
  if (isFree) price.classList.add("ia-gift-chooser__price--free");
  price.textContent = priceText;

  card.append(media, info, price);
  return card;
}

if (!customElements.get("ia-cart-tiers")) {
  customElements.define("ia-cart-tiers", IaCartTiers);
}
